/**
 * Curated starter decks shipped with Lexibit. Users browse them in /library
 * and "install" a copy into their personal decks (creating a Deck + Cards
 * via the store's installStarterDeck mutator).
 *
 * Small hand-curated decks can keep cards inline. Large catalogs should expose
 * lightweight deck metadata and lazy `loadCards()` functions so the Library can
 * list thousands of dictionaries without loading every card into the app shell.
 */

import type {
  AgeGroupId,
  CefrLevelId,
  EntityTypeId,
  LanguageId,
  TopicId,
} from "../references";
import type { PackLocalizedText } from "../packs";

export type StarterCard = {
  front: string;
  frontAliases?: string[];
  back: string;
  backAliases?: string[];
  entityType?: EntityTypeId;
  classification?: {
    topic?: TopicId;
    ageGroup?: AgeGroupId;
    level?: CefrLevelId;
  };
};

/** `name`/`description` (+ their `_en`/`_ru` counterparts) use the same
    bilingual scheme as the generated pack registry — see
    {@link PackLocalizedText} — since `scripts/build-hand-packs.ts` copies
    these fields straight into `public/packs/index.json` entries. */
export type StarterDeck = PackLocalizedText & {
  /** Stable kebab-case id — used as a "do not re-install" hint someday. */
  id: string;
  /** Optional versioned catalog provenance persisted atomically with the deck. */
  catalogProvenance?: {
    key: string;
    packVersion: number;
    cardCount: number;
  };
  sourceLang: LanguageId;
  targetLang: LanguageId;
  topic: TopicId;
  ageGroup: AgeGroupId;
  level: CefrLevelId;
  /** Author or origin attribution shown small under the name. */
  author?: string;
  /** Total cards shown in Library without materializing a lazy deck. */
  cardCount: number;
  /** Inline cards for small decks. Large generated decks should omit this. */
  cards?: StarterCard[];
  /** Lazy loader for large generated decks. */
  loadCards?: () => Promise<StarterCard[]>;
};

export function getStarterDeckCardCount(deck: StarterDeck): number {
  return deck.cardCount;
}

export async function loadStarterDeckCards(deck: StarterDeck): Promise<StarterCard[]> {
  if (deck.cards) return deck.cards;
  if (deck.loadCards) return deck.loadCards();
  return [];
}
